/**
 * Tenant Branding Asset URLs
 *
 * Computes the fixed, predictable URLs for a tenant's branding assets served by
 * `BaseTenantBrandingAssetsController` (BizFirst.IAM Consolidated WebApi). The backend
 * resolves "which tenant" from the request's `Host` header — no slug/environment needs
 * to be encoded into the URL — so this is pure string computation, not a fetch. All
 * three endpoints are anonymous and never 404 (tenant-specific -> platform-default ->
 * embedded placeholder fallback happens server-side), so no client-side
 * fetch/caching/error-handling machinery is needed on top of these URLs.
 */

export interface TenantBrandingUrls {
  /** Tenant's logo image. Never 404s. Safe to point an `<img src>` directly at this URL. */
  logoUrl: string
  /** Tenant's custom theme CSS. Never 404s (200 with an empty body when unset). Safe to
   * point a `<link rel="stylesheet" href>` directly at this URL. */
  themeCssUrl: string
  /** Tenant's custom JS. Never 404s (200 with an empty body when unset). Safe to point a
   * `<script src>` directly at this URL. */
  scriptJsUrl: string
}

/**
 * Computes the 3 tenant branding asset URLs from a base API URL.
 *
 * @param apiBaseUrl - Base URL of the BizFirst.IAM Consolidated WebApi (e.g.
 *   `https://localhost:10001`). Trailing slashes are trimmed.
 *
 * @example
 * ```ts
 * const { logoUrl, themeCssUrl, scriptJsUrl } = getTenantBrandingUrls('https://localhost:10001')
 * // logoUrl      === 'https://localhost:10001/api/assets/images/logo'
 * // themeCssUrl  === 'https://localhost:10001/api/assets/css/theme.css'
 * // scriptJsUrl  === 'https://localhost:10001/api/assets/scripts/main.js'
 * ```
 */
export function getTenantBrandingUrls(apiBaseUrl: string): TenantBrandingUrls {
  const base = apiBaseUrl.replace(/\/+$/, '')
  return {
    logoUrl: `${base}/api/assets/images/logo`,
    themeCssUrl: `${base}/api/assets/css/theme.css`,
    scriptJsUrl: `${base}/api/assets/scripts/main.js`,
  }
}

export default getTenantBrandingUrls
